import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NewsCard from "../components/NewsCard";
import AdSenseAd from "../components/AdSenseAd";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/Home.css";

const categories = ["Home", "Updates", "Education", "Entertainment"];

const CATEGORY_API_MAP = {
  // Updates: "https://jobs.mpdatahub.com/api/newsarticles",
  Education: "https://jobs.mpdatahub.com/api/study-articles",
  Entertainment: "https://jobs.mpdatahub.com/api/movienewsartcles",
  Insights: "https://jobs.mpdatahub.com/api/getScientificFacts",
  Technology: "https://jobs.mpdatahub.com/api/technews",
  Business: "https://jobs.mpdatahub.com/api/newsarticles",
  Health: "https://jobs.mpdatahub.com/api/getHealthArticles",
  Sports: "https://jobs.mpdatahub.com/api/getSportsArticles",
  Education: "https://jobs.mpdatahub.com/api/study-articles",
  Stories: "https://jobs.mpdatahub.com/api/getShortStories",
  Updates: "https://jobs.mpdatahub.com/api/tech-updates"
};

const ARTICLES_PER_PAGE = 20;
const ARTICLES_PER_CATEGORY = 3;
const AD_POSITIONS = [4, 10];

const Home = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const searchInputRef = useRef(null);
  const categoryList = useMemo(() => categories.slice(1), []);
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!categoryName || categoryName === "Home") {
          const fetchPromises = categoryList.map(async (category) => {
            try {
              const apiUrl = CATEGORY_API_MAP[category];
              if (!apiUrl) throw new Error(`No API found for ${category}`);
              const response = await fetch(apiUrl);
              if (!response.ok) throw new Error(`Failed to fetch ${category}`);
              const data = await response.json();
              const articles = Array.isArray(data) ? data : data.data || data.articles || [];
              return articles.map(article => ({
                ...article,
                category: category,
                date: article.date || article.createdAt || article.publishedAt || new Date().toISOString(),
                id: article.id || article._id || `${category}-${Math.random().toString(36).substr(2, 9)}`,
                title: article.title || article.heading || "",
                description: article.description || article.summary || "",
                imageUrl: article.imageUrl || article.image || article.thumbnail || "",
              }));
            } catch (err) {
              console.warn(`Error fetching ${category}:`, err);
              return [];
            }
          });
          const results = await Promise.allSettled(fetchPromises);
          let combinedArticles = [];
          results.forEach(result => {
            if (result.status === "fulfilled") {
              combinedArticles = [...combinedArticles, ...result.value];
            }
          });
          combinedArticles.sort((a, b) => new Date(b.date) - new Date(a.date));
          setAllArticles(combinedArticles);
        } else {
          const apiUrl = CATEGORY_API_MAP[categoryName];
          if (!apiUrl) throw new Error(`No API found for ${categoryName}`);
          const response = await fetch(apiUrl);
          if (!response.ok) throw new Error(`Failed to fetch ${categoryName}`);
          const data = await response.json();
          const articles = Array.isArray(data) ? data : data.data || data.articles || [];
          const formattedArticles = articles.map(article => ({
            ...article,
            category: categoryName,
            date: article.date || article.createdAt || article.publishedAt || new Date().toISOString(),
            id: article.id || article._id || `${categoryName}-${Math.random().toString(36).substr(2, 9)}`,
            title: article.title || article.heading || "",
            description: article.description || article.summary || "",
            imageUrl: article.imageUrl || article.image || article.thumbnail || "",
          }));
          formattedArticles.sort((a, b) => new Date(b.date) - new Date(a.date));
          setAllArticles(formattedArticles);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(err.message);
        setAllArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [categoryName, categoryList]);

  const getTimeAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }, []);

  useEffect(() => {
    if (isMobileSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isMobileSearchExpanded]);

  const displayedArticles = useMemo(() => {
    let filtered = allArticles;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title?.toLowerCase().includes(query) ||
        article.description?.toLowerCase().includes(query) ||
        article.content?.toLowerCase().includes(query)
      );
    }
    return [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allArticles, searchQuery]);

  const articlesByCategory = useMemo(() => {
    if (categoryName && categoryName !== "Home") return {};
    const grouped = {};
    categoryList.forEach(category => {
      const categoryArticles = allArticles
        .filter(article => article.category === category)
        .slice(0, ARTICLES_PER_CATEGORY);

      if (categoryArticles.length > 0) {
        grouped[category] = categoryArticles;
      }
    });
    return grouped;
  }, [allArticles, categoryName, categoryList]);

  const totalPages = Math.ceil(displayedArticles.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const endIndex = startIndex + ARTICLES_PER_PAGE;
  const paginatedArticles = displayedArticles.slice(startIndex, endIndex);
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryName, searchQuery]);

  const renderArticlesWithAds = useCallback(() => {
    const elements = [];
    paginatedArticles.forEach((article, index) => {
      elements.push(
        <NewsCard
          key={article.id}
          article={article}
          timeAgo={getTimeAgo(article.date)}
        />
      );
      if (AD_POSITIONS.includes(index + 1) && paginatedArticles.length >= 5) {
        elements.push(
          <div key={`ad-${index}`} className="in-content-ad">
            <AdSenseAd
              slot="3581145953"
              style={{ display: "block", width: "100%", height: "250px" }}
              format="display"
            />
          </div>
        );
      }
    });
    return elements;
  }, [paginatedArticles, getTimeAgo]);

  const renderCategorySections = useCallback(() => {
    return Object.entries(articlesByCategory).map(([category, articles]) => (
      <div key={category} className="category-section">
        <div className="category-section-header">
          <h2 className="category-section-title">{category}</h2>
          <button className="show-all-button" onClick={() => navigate(`/${category}`)}>
            Show All
          </button>
        </div>
        <div className="category-articles-grid">
          {articles.map((article) => (
            <NewsCard
              key={article.id}
              article={article}
              timeAgo={getTimeAgo(article.date)}
              compact={true}
            />
          ))}
        </div>
      </div>
    ));
  }, [articlesByCategory, getTimeAgo, navigate]);

  const latestNews = useMemo(() => allArticles.slice(0, 5), [allArticles]);
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const toggleMobileSearch = () => {
    setIsMobileSearchExpanded(!isMobileSearchExpanded);
  };
  const handleSearchClear = () => {
    setSearchQuery("");
    if (window.innerWidth < 768) {
      setIsMobileSearchExpanded(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullscreen message="Loading news articles..." />;
  }

  return (
    <div className="home-wrapper">
      <div className="main-content">
        <div className={`home-container ${categoryName ? "category-view" : ""}`}>
          <div className="content">
            <div className="category-header">
              <div className="header-top-row">
                <h1 className="category-title">{categoryName || "Home"}</h1>
                <button className="mobile-search-toggle" onClick={toggleMobileSearch} aria-label="Toggle search">
                  <i className="fas fa-search"></i>
                </button>
              </div>

              <div className={`search-containertop ${isMobileSearchExpanded ? "expanded" : ""}`}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="search-inputtop"
                />
                {searchQuery && (
                  <button className="search-cleartop" onClick={handleSearchClear} aria-label="Clear search">
                    ×
                  </button>
                )}
              </div>
            </div>

            {(!categoryName || categoryName === "Home") && Object.keys(articlesByCategory).length > 0 ? (
              <div className="home-categories-view">
                {renderCategorySections()}
              </div>
            ) : (
              <>
                {displayedArticles.length === 0 ? (
                  <div className="no-articles">
                    <p>
                      {searchQuery
                        ? `No articles found for "${searchQuery}"`
                        : "No articles found for this category"}
                    </p>
                    {searchQuery && (
                      <button onClick={handleSearchClear} className="clear-search-button">
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {totalPages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    )}
                    <div className="articles-grid">{renderArticlesWithAds()}</div>
                    {totalPages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Pagination component
const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
      pages.push(i);
    }
  }

  return (
    <div className="pagination">
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="nav-button"
        aria-label="First page"
      >
        &laquo;
      </button>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="nav-button"
        aria-label="Previous page"
      >
        &lsaquo;
      </button>
      {pages.map((page, index) => (
        <React.Fragment key={page}>
          {index > 0 && pages[index - 1] < page - 1 && (
            <span className="ellipsis">...</span>
          )}
          <button
            onClick={() => onPageChange(page)}
            className={currentPage === page ? "active" : ""}
            aria-label={`Page ${page}`}
          >
            {page}
          </button>
        </React.Fragment>
      ))}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="nav-button"
        aria-label="Next page"
      >
        &rsaquo;
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="nav-button"
        aria-label="Last page"
      >
        &raquo;
      </button>
    </div>
  );
};

export default Home;